"use client";

import React, { useState, useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { Moon, Sun, LogOut, User, Settings, Sparkles, Building2, Warehouse, Crown } from "lucide-react";
import { supabase } from "@/lib/supabase";

// ===================== Interfaces =====================
interface SupabaseUser {
  id: string;
  email?: string;
}


const isPrivilegedRole = (role: string) => {
  const r = (role || "").toLowerCase();
  return (
    r.includes("owner") ||
    r.includes("due") || // dueño / dueno
    r.includes("dueno") ||
    r.includes("admin") ||
    r.includes("super")
  );
};

/** Traducción de nombres de rol al español */
const ROLE_ES: Record<string, string> = {
  admin: "Administrador",
  owner: "Dueño",
  manager: "Gerente",
  seller: "Vendedor",
  warehouse_worker: "Almacenista",
};

function translateRole(roleName: string): string {
  if (!roleName) return "Usuario";
  const key = roleName.trim().toLowerCase();
  return ROLE_ES[key] || roleName;
}

/** Simplificar nombre de plan para mostrar en el header */
function displayPlanName(planName: string): string {
  if (!planName) return "Free";
  const lower = planName.toLowerCase();
  // Free se queda como está
  if (lower.includes("free")) return "Free";
  // Todos los demás planes (299, 499, 699) se muestran como "Pro"
  return "Pro";
}


// ===================== ThemeToggle =====================
function ThemeToggle() {
  const [theme, setTheme] = useState<"light" | "dark">("light");

  useEffect(() => {
    const stored = localStorage.getItem("theme") as "light" | "dark" | null;
    if (stored) {
      setTheme(stored);
      document.documentElement.classList.toggle("dark", stored === "dark");
    } else {
      const hour = new Date().getHours();
      const isNight = hour < 6 || hour > 20;
      const autoTheme = isNight ? "dark" : "light";
      setTheme(autoTheme);
      document.documentElement.classList.toggle("dark", autoTheme === "dark");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    localStorage.setItem("theme", newTheme);
  };

  return (
    <button
      onClick={toggleTheme}
      className="relative p-2 rounded-xl bg-gradient-to-br from-white/20 to-white/5 dark:from-slate-900/30 dark:to-slate-800/30 backdrop-blur-xl border border-white/30 dark:border-slate-700/50 transition-all duration-300 group neon-glow"
      aria-label={`Cambiar a tema ${theme === "dark" ? "claro" : "oscuro"}`}
    >
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-cyan-500/10 dark:to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="relative">
        {theme === "dark" ? (
          <Sun className="w-4 h-4 text-yellow-300" />
        ) : (
          <Moon className="w-4 h-4 text-indigo-300" />
        )}
      </div>
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-10" />
    </button>
  );
}

// ===================== Neon Elements =====================
// ===================== Header principal =====================
export default function Header() {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [businessTypeName, setBusinessTypeName] = useState<string>("Cargando...");
  const [companyName, setCompanyName] = useState<string>("Mi negocio");
  const [fullName, setFullName] = useState<string>("");
  const [roleName, setRoleName] = useState<string>("");
  const [activeWarehouseId, setActiveWarehouseId] = useState<string>("");
  const [activeWarehouseName, setActiveWarehouseName] = useState<string>("");
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [canSwitchWarehouse, setCanSwitchWarehouse] = useState(false);
  const [planName, setPlanName] = useState<string>("");
  const [mounted, setMounted] = useState(false);

  const [warehouseConfirmOpen, setWarehouseConfirmOpen] = useState(false);
  const [warehouseConfirmSeverity, setWarehouseConfirmSeverity] = useState<"danger" | "warning" | "success" | "info">("warning");
  const [logoutCashModalOpen, setLogoutCashModalOpen] = useState(false);
  const [logoutCheckingCash, setLogoutCheckingCash] = useState(false);
  const [logoutCashMessage, setLogoutCashMessage] = useState("");
  const pendingWarehouseRef = useRef<{ id: string; name: string } | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const lockWarehouseSwitch = (pathname || "").startsWith("/almacen");

  const lockWarehouseWidget = [
    "/almacen",
    "/caja",
    "/reportes",
    "/proveedores",
    "/config",
  ].some((base) => pathname === base || (pathname || "").startsWith(base + "/"));
  useEffect(() => {
    setMounted(true);
  }, []);

  // Cargar usuario y datos
  useEffect(() => {
    const loadUserAndGiro = async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error("Error fetching session:", sessionError);
          setSupabaseUser(null);
          setBusinessTypeName("Sin sesión");
          setCompanyName("Mi negocio");
          setFullName("");
          setRoleName("");
          return;
        }

        const user = sessionData?.session?.user;
        if (!user) {
          setSupabaseUser(null);
          setBusinessTypeName("Sin sesión");
          setCompanyName("Mi negocio");
          setFullName("");
          setRoleName("");
          return;
        }

        setSupabaseUser(user);

        // Obtener perfil del usuario
        const { data: profile, error: profileErr } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profileErr) {
          console.error("Error fetching profile:", profileErr);
          setBusinessTypeName("Error al cargar giro");
          return;
        }

        // Determine whether user can switch warehouses (admin/owner)
        const profileRoleHint = String((profile as any)?.role || (profile as any)?.role_name || "");
        const canSwitchFromProfile = Boolean((profile as any)?.is_owner || (profile as any)?.is_admin) || isPrivilegedRole(profileRoleHint);
        setCanSwitchWarehouse(canSwitchFromProfile);

        const rawFullName = (profile as any)?.full_name;
        if (typeof rawFullName === "string" && rawFullName.trim().length > 0) {
          setFullName(rawFullName.trim());
        } else {
          setFullName("Usuario");
        }

        let resolvedRoleName = "";
        const roleId = (profile as any)?.role_id as string | undefined;
        if (roleId) {
          const { data: role, error: roleErr } = await supabase
            .from("roles")
            .select("name")
            .eq("id", roleId)
            .maybeSingle();

          if (roleErr) {
            console.error("Error fetching role:", roleErr);
            setRoleName("Sin rol");
            setCanSwitchWarehouse(canSwitchFromProfile);
          } else {
            const rawRoleName = (role as any)?.name;
            resolvedRoleName = typeof rawRoleName === "string" && rawRoleName.trim().length > 0 ? rawRoleName.trim() : "";
            setRoleName(resolvedRoleName || "Sin rol");
            setCanSwitchWarehouse(isPrivilegedRole(resolvedRoleName) || canSwitchFromProfile);
          }
        } else {
          setRoleName("Sin rol");
          setCanSwitchWarehouse(canSwitchFromProfile);
        }

        const tenantId = (profile as any)?.tenant_id as string | undefined;
        if (tenantId) {
          const { data: tenant, error: tenantErr } = await supabase
            .from("tenants")
            .select("company_name")
            .eq("id", tenantId)
            .maybeSingle();

          if (tenantErr) {
            console.error("Error fetching tenant:", tenantErr);
            setCompanyName("Mi negocio");
          } else {
            const rawCompanyName = (tenant as any)?.company_name;
            if (typeof rawCompanyName === "string" && rawCompanyName.trim().length > 0) {
              setCompanyName(rawCompanyName.trim());
            } else {
              setCompanyName("Mi negocio");
            }
          }
        } else {
          setCompanyName("Mi negocio");
        }

        try {
          if (tenantId) localStorage.setItem("active_tenant_id", tenantId);
          if (user?.id) localStorage.setItem("active_user_id", user.id);
          const profileBusinessTypeId = String((profile as any)?.business_type_id || "");
          if (profileBusinessTypeId) {
            localStorage.setItem("active_business_type_id", profileBusinessTypeId);
            localStorage.setItem("business_type_id", profileBusinessTypeId);
          }
        } catch {}

        // Cargar nombre del plan activo
        if (tenantId) {
          const { data: tpRow } = await supabase
            .from("tenant_plans")
            .select("plan_id, plans(name)")
            .eq("tenant_id", tenantId)
            .eq("is_active", true)
            .limit(1)
            .maybeSingle();

          const rawPlanName = (tpRow as any)?.plans?.name ?? (tpRow as any)?.plans?.[0]?.name ?? "";
          setPlanName(typeof rawPlanName === "string" && rawPlanName.trim().length > 0 ? rawPlanName.trim() : "Free");
        } else {
          setPlanName("Free");
        }

        // Cargar almacenes del tenant y resolver almacén activo
        if (tenantId) {
          const { data: whs, error: whErr } = await supabase
            .from("warehouses")
            .select("id,name")
            .eq("tenant_id", tenantId)
            .order("name", { ascending: true });

          if (whErr) {
            console.error("Error fetching warehouses:", whErr);
            setWarehouses([]);
          } else {
            const list = (whs as any[] | null) || [];
            const normalized = list
              .map((w) => ({ id: String((w as any).id), name: String((w as any).name || "Almacén") }))
              .filter((w) => w.id && w.name);
            setWarehouses(normalized);

            // Resolver almacén activo: localStorage > profile > primer almacén
            let storedId = "";
            let storedName = "";
            try {
              storedId = localStorage.getItem("active_warehouse_id") || localStorage.getItem("warehouse_id") || "";
              storedName = localStorage.getItem("active_warehouse_name") || localStorage.getItem("warehouse_name") || "";
            } catch {}

            const profileWhId = String((profile as any)?.warehouse_id || (profile as any)?.default_warehouse_id || "");
            const canUseStoredWarehouse = isPrivilegedRole(resolvedRoleName) || canSwitchFromProfile;
            const storedIsValid = Boolean(storedId && normalized.some((w) => w.id === storedId));
            const pickedId =
              canUseStoredWarehouse && storedIsValid
                ? storedId
                : profileWhId || normalized?.[0]?.id || "";
            const pickedName =
              normalized.find((w) => w.id === pickedId)?.name ||
              (pickedId === storedId ? storedName : "") ||
              normalized?.[0]?.name ||
              "";

            if (pickedId) {
              setActiveWarehouseId(pickedId);
              setActiveWarehouseName(pickedName);
              try {
                localStorage.setItem("active_warehouse_id", pickedId);
                if (pickedName) localStorage.setItem("active_warehouse_name", pickedName);
                // Backward-compatible keys
                localStorage.setItem("warehouse_id", pickedId);
                if (pickedName) localStorage.setItem("warehouse_name", pickedName);
              } catch {}
            }
          }
        }

        if (!profile?.business_type_id) {
          setBusinessTypeName("Sin giro");
          return;
        }

        const { data: businessType, error: businessErr } = await supabase
          .from("business_types")
          .select("name")
          .eq("id", profile.business_type_id)
          .maybeSingle();

        if (businessErr) {
          console.error("Error fetching business type:", businessErr);
          setBusinessTypeName("Error al cargar giro");
          return;
        }

        setBusinessTypeName(businessType?.name ?? "Sin giro asignado");
      } catch (err) {
        console.error("Unexpected error:", err);
        setBusinessTypeName("Error al cargar");
      }
    };

    loadUserAndGiro();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        setSupabaseUser(null);
        setBusinessTypeName("Sin sesión");
        setCompanyName("Mi negocio");
        setFullName("");
        setRoleName("");
      } else {
        loadUserAndGiro();
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

    const applyWarehouseChange = (next: { id: string; name: string }) => {
// Persist new active warehouse
    setActiveWarehouseId(next.id);
    setActiveWarehouseName(next.name);

    try {
      localStorage.setItem("active_warehouse_id", next.id);
      localStorage.setItem("active_warehouse_name", next.name);
      // Backward-compatible keys
      localStorage.setItem("warehouse_id", next.id);
      localStorage.setItem("warehouse_name", next.name);
    } catch {}

    // Reset warehouse-scoped context to prevent scope mismatches (cash session, cart, customer)
    try {
      // Remove known keys
      const keysToRemove = [
        "cash_session_id",
        "cashSessionId",
        "pos_cash_session_id",
        "active_cash_session_id",
        "cash_register_session_id",
        "cash_register_open",
        "cash_register_state",
        "pos_cart",
        "pos_customer_id",
        "active_customer_id",
      ];
      keysToRemove.forEach((k) => localStorage.removeItem(k));

      // Remove any custom cash/caja keys (keep auth/session keys untouched)
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i) || "";
        if (!k) continue;
        const lk = k.toLowerCase();
        if (lk.includes("cash") || lk.includes("caja")) {
          localStorage.removeItem(k);
        }
      }
    } catch {}

    // Notify pages to refresh their warehouse-scoped data
    try {
      window.dispatchEvent(
        new CustomEvent("warehouse:changed", { detail: { warehouseId: next.id, warehouseName: next.name } })
      );
      window.dispatchEvent(new Event("cash:reset"));
      window.dispatchEvent(new Event("cart:reset"));
      window.dispatchEvent(new Event("customer:reset"));
    } catch {}

    // Hard reload is the most reliable way to guarantee fresh data in PWA/mobile contexts.
    window.location.reload();
  };

  const confirmWarehouseChange = () => {
    const next = pendingWarehouseRef.current;
    setWarehouseConfirmOpen(false);
    pendingWarehouseRef.current = null;
    if (!next) return;
    applyWarehouseChange(next);
  };

  const cancelWarehouseChange = () => {
    setWarehouseConfirmOpen(false);
    pendingWarehouseRef.current = null;
  };

const handleWarehouseChange = (nextId: string) => {
    const next = warehouses.find((w) => w.id === nextId);
    if (!next) return;

    // If there's an open cash session in the current warehouse context, warn the user.
    let hasCashContext = false;
    try {
      // Known keys (keep these even if some modules use different names)
      const knownCashKeys = [
        "cash_session_id",
        "cashSessionId",
        "pos_cash_session_id",
        "active_cash_session_id",
        "cash_register_session_id",
        "cash_register_open",
        "cash_register_state",
      ];
      hasCashContext = knownCashKeys.some((k) => Boolean(localStorage.getItem(k)));

      // Also detect any custom cash/caja keys
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || "";
        if (!k) continue;
        if (k.toLowerCase().includes("cash") || k.toLowerCase().includes("caja")) {
          hasCashContext = true;
          break;
        }
      }
    } catch {}

    if (hasCashContext) {
      pendingWarehouseRef.current = { id: next.id, name: next.name };
      setWarehouseConfirmSeverity("warning");
      setWarehouseConfirmOpen(true);
      return;
    }

        applyWarehouseChange(next);
  };

  const getAuthHeaders = async () => {
    const { data } = await supabase.auth.getSession();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (data.session?.access_token) {
      headers.Authorization = `Bearer ${data.session.access_token}`;
    }
    return headers;
  };

  const checkOpenCashSession = async () => {
    let tenantId = "";
    let warehouseId = activeWarehouseId || "";
    try {
      tenantId = localStorage.getItem("active_tenant_id") || localStorage.getItem("tenant_id") || "";
      warehouseId =
        warehouseId ||
        localStorage.getItem("active_warehouse_id") ||
        localStorage.getItem("warehouse_id") ||
        "";
    } catch {}

    if (!tenantId || !warehouseId) return false;

    const qs = new URLSearchParams({ tenant_id: tenantId, warehouse_id: warehouseId });
    const res = await fetch(`/api/cash-register/status?${qs.toString()}`, {
      method: "GET",
      cache: "no-store",
      headers: await getAuthHeaders(),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || "No pude revisar si hay caja abierta.");
    }

    return Boolean(data?.open);
  };

  const performLogout = async () => {
    try {
      try {
        localStorage.removeItem("pos.pendingLogoutAfterCashClose");
      } catch {}
      await supabase.auth.signOut();
      router.replace("/auth/login");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  const goCloseCashBeforeLogout = () => {
    try {
      localStorage.setItem("pos.pendingLogoutAfterCashClose", "1");
    } catch {}
    setLogoutCashModalOpen(false);
    setIsProfileOpen(false);
    router.push("/caja");
  };

  const handleLogout = async () => {
    if (logoutCheckingCash) return;

    setLogoutCashMessage("");
    setLogoutCheckingCash(true);
    try {
      const hasOpenCash = await checkOpenCashSession();
      if (hasOpenCash) {
        setLogoutCashModalOpen(true);
        setIsProfileOpen(false);
        return;
      }

      await performLogout();
    } catch (err: any) {
      setLogoutCashMessage(err?.message || "No pude revisar la caja abierta.");
      setLogoutCashModalOpen(true);
      setIsProfileOpen(false);
    } finally {
      setLogoutCheckingCash(false);
    }
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    if (!isProfileOpen) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsProfileOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isProfileOpen]);

  if (!mounted) return null;

  const warehouseModalStyles = {
    danger: {
      wrap: "bg-gradient-to-br from-rose-950/70 via-fuchsia-950/40 to-slate-950/30",
      ring: "bg-rose-500/15 border-rose-400/30",
      icon: "text-rose-200",
      button: "bg-rose-600 hover:bg-rose-500 focus:ring-rose-400/50",
    },
    warning: {
      wrap: "bg-gradient-to-br from-amber-950/70 via-orange-950/40 to-slate-950/30",
      ring: "bg-amber-500/15 border-amber-400/30",
      icon: "text-amber-200",
      button: "bg-amber-600 hover:bg-amber-500 focus:ring-amber-400/50",
    },
    success: {
      wrap: "bg-gradient-to-br from-emerald-950/70 via-teal-950/40 to-slate-950/30",
      ring: "bg-emerald-500/15 border-emerald-400/30",
      icon: "text-emerald-200",
      button: "bg-emerald-600 hover:bg-emerald-500 focus:ring-emerald-400/50",
    },
    info: {
      wrap: "bg-gradient-to-br from-sky-950/70 via-blue-950/40 to-slate-950/30",
      ring: "bg-sky-500/15 border-sky-400/30",
      icon: "text-sky-200",
      button: "bg-sky-600 hover:bg-sky-500 focus:ring-sky-400/50",
    },
  } as const;

  const warehouseModal = warehouseModalStyles[warehouseConfirmSeverity];

  return (
    <>
      <header
        data-app-navbar
        className="fixed inset-x-0 top-0 z-50 w-full border-b border-white/20 dark:border-slate-700/30 bg-gradient-to-b from-white/70 via-white/50 to-white/30 dark:from-slate-900/80 dark:via-slate-900/60 dark:to-slate-900/40 backdrop-blur-2xl"
        role="banner"
      >
        <div className="relative py-1.5 pl-16 pr-3 md:px-6 flex items-center justify-between">
          {/* Lado izquierdo: Logo y nombre de empresa */}
          <div className="flex items-center space-x-4 min-w-0 flex-1">
            {/* Logo con efecto glass */}
            <div className="relative group">
              <div className="relative p-1 rounded-2xl bg-gradient-to-br from-white/30 to-white/10 dark:from-slate-800/40 dark:to-slate-900/30 backdrop-blur-xl border border-white/40 dark:border-slate-700/50">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="w-4 h-4 rounded-xl bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600" />
              </div>
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500 -z-10" />
            </div>

            {/* Nombre de empresa con efecto neón */}
            <div className="relative min-w-0">
              <h1 className="text-lg font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent truncate max-w-[14rem] sm:max-w-[18rem] md:max-w-[24rem]">
                {companyName}
              </h1>
              <div className="flex items-center flex-wrap gap-2 mt-1">
                <div className="hidden md:flex items-center space-x-1 px-2 py-1 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-400/20">
                  <Building2 className="w-3 h-3 text-blue-500" />
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                    {businessTypeName}
                  </span>
                </div>
                {/* Almacén activo */}
                {!lockWarehouseWidget && (
                  <div className="hidden md:flex items-center space-x-1 px-2 py-1 rounded-lg bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 backdrop-blur-sm border border-emerald-400/20">
                  <Warehouse className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                  {canSwitchWarehouse && warehouses.length > 1 ? (
                    <select
                      value={activeWarehouseId || ""}
                      onChange={(e) => handleWarehouseChange(e.target.value)}
                      className="text-xs font-semibold text-emerald-800 dark:text-emerald-200 bg-transparent outline-none cursor-pointer"
                    >
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id} className="text-slate-900">
                          {w.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                      {activeWarehouseName || "Almacén"}
                    </span>
                  )}
                  </div>
                )}
                <div className={`w-2 h-2 rounded-full ${supabaseUser ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
                <span className="hidden md:inline text-xs text-slate-600 dark:text-slate-400">
                  {supabaseUser ? "Conectado" : "Desconectado"}
                </span>
              </div>
            </div>
          </div>

          {/* Lado derecho: Acciones y perfil */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            {/* Etiqueta de plan */}
            {supabaseUser && planName && (
              <div className={`hidden md:flex items-center space-x-1.5 px-2.5 py-1.5 rounded-xl backdrop-blur-sm border ${
                displayPlanName(planName) === "Free"
                  ? "bg-gradient-to-r from-gray-500/10 to-slate-500/10 border-gray-400/20"
                  : "bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-yellow-400/20"
              }`}>
                <Crown className={`w-3.5 h-3.5 ${
                  displayPlanName(planName) === "Free"
                    ? "text-gray-500"
                    : "text-yellow-500"
                }`} />
                <span className={`text-xs font-semibold ${
                  displayPlanName(planName) === "Free"
                    ? "text-gray-700 dark:text-gray-300"
                    : "text-yellow-700 dark:text-yellow-300"
                }`}>
                  {displayPlanName(planName)}
                </span>
              </div>
            )}

            {/* Botón de tema */}
            <ThemeToggle />

            {/* Perfil de usuario */}
            {!supabaseUser ? (
              <button
                onClick={() => router.push("/auth/login")}
                className="relative px-3 py-2 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white font-semibold text-sm transition-all duration-300 group neon-button"
              >
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Iniciar sesión</span>
                </span>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-30 group-hover:opacity-70 transition-opacity duration-500 -z-10" />
              </button>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="relative flex items-center space-x-3 p-2 rounded-2xl bg-gradient-to-br from-white/30 to-white/10 dark:from-slate-800/40 dark:to-slate-900/30 backdrop-blur-xl border border-white/40 dark:border-slate-700/50 transition-all duration-300 group"
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Avatar con efecto neón */}
                  <div className="relative">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500 -z-10" />
                  </div>

                  
                  {/* Información del usuario */}
                  <div className="text-left hidden lg:block">
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">
                      {fullName || "Usuario"}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-300 truncate max-w-[160px]">
                      {supabaseUser.email}
                    </p>
                  </div>
                  
                  {/* Ícono de flecha */}
                  <div className={`transform transition-transform duration-300 ${isProfileOpen ? "rotate-180" : ""}`}>
                    <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Dropdown de perfil */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-72 rounded-2xl bg-gradient-to-b from-white/90 to-white/70 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-2xl border border-white/40 dark:border-slate-700/50 animate-in fade-in slide-in-from-top-2">
                    {/* Encabezado del perfil */}
                    <div className="p-4 border-b border-white/30 dark:border-slate-700/50">
                      <div className="flex items-center space-x-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-slate-800 dark:text-white truncate">{fullName || "Usuario"}</h3>
                          <p className="text-sm text-slate-600 dark:text-slate-300 truncate">{supabaseUser.email}</p>
                          <div className="flex items-center flex-wrap gap-2 mt-1">
                            <span className="inline-flex px-2 py-1 text-xs rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-700 dark:text-blue-300">
                              {roleName ? translateRole(roleName) : "Sin rol"}
                            </span>
                            {planName && (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg ${
                                displayPlanName(planName) === "Free"
                                  ? "bg-gradient-to-r from-gray-500/20 to-slate-500/20 text-gray-700 dark:text-gray-300"
                                  : "bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-yellow-700 dark:text-yellow-300"
                              }`}>
                                <Crown className="w-3 h-3" />
                                {displayPlanName(planName)}
                              </span>
                            )}
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />

                            {/* Mobile only: switch warehouse inside profile dropdown */}
                            {!lockWarehouseWidget && (
                            <div className="md:hidden mt-2 w-full flex items-center gap-2 px-2 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 backdrop-blur-sm border border-emerald-400/20">
                              <Warehouse className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                              {!lockWarehouseSwitch && canSwitchWarehouse && warehouses.length > 1 ? (
                                <select
                                  value={activeWarehouseId || ""}
                                  onChange={(e) => handleWarehouseChange(e.target.value)}
                                  className="flex-1 text-xs font-semibold text-emerald-800 dark:text-emerald-200 bg-transparent outline-none cursor-pointer"
                                >
                                  {warehouses.map((w) => (
                                    <option key={w.id} value={w.id} className="text-slate-900">
                                      {w.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="flex-1 text-xs font-semibold text-emerald-800 dark:text-emerald-200 truncate">
                                  {activeWarehouseName || "Almacén"}
                                </span>
                              )}
                            </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Opciones del menú */}
                    <div className="p-2 space-y-1">
                      <button className="w-full flex items-center space-x-3 p-2.5 rounded-xl hover:bg-white/50 dark:hover:bg-slate-700/50 transition-all duration-200 group">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20">
                          <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Mi perfil</span>
                      </button>
                      
                      <button className="w-full flex items-center space-x-3 p-2.5 rounded-xl hover:bg-white/50 dark:hover:bg-slate-700/50 transition-all duration-200 group">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-600/20">
                          <Settings className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Configuración</span>
                      </button>
                      
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 p-2.5 rounded-xl hover:bg-red-500/10 transition-all duration-200 group border-t border-white/30 dark:border-slate-700/50 mt-2 pt-2"
                      >
                        <div className="p-2 rounded-lg bg-gradient-to-br from-red-500/20 to-red-600/20">
                          <LogOut className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </div>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">Cerrar sesión</span>
                      </button>
                    </div>

                    {/* Pie con efecto neón */}
                    <div className="p-3 border-t border-white/30 dark:border-slate-700/50 bg-gradient-to-r from-transparent via-blue-500/5 to-transparent">
                      <div className="text-center">
                        <span className="text-xs text-slate-500 dark:text-slate-400">Sistema activo • v2.0</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Barra inferior con efecto neón */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 via-30% via-purple-400/50 to-transparent animate-pulse" />
      </header>
      
      {/* Espacio para el header fijo */}
      <div aria-hidden="true" className="h-[60px]" />

      {/* Estilos globales para efectos glass y neón */}
      <style jsx>{`
        .neon-glow:hover {
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(147, 51, 234, 0.2);
        }
        
        .neon-button {
          box-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
        }
        
        .neon-button:hover {
          box-shadow: 0 0 25px rgba(59, 130, 246, 0.6), 0 0 35px rgba(147, 51, 234, 0.4);
        }
        
        /* Transiciones suaves para tema oscuro */
        html {
          transition: background-color 0.5s cubic-bezier(0.4, 0, 0.2, 1), color 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Efectos de vidrio para tema oscuro */
        .dark .glass-effect {
          background: rgba(30, 41, 59, 0.7);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        /* Efectos de vidrio para tema claro */
        .glass-effect {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        /* Animación de entrada para elementos */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-in {
          animation: fadeIn 0.2s ease-out;
        }
        
        /* Efecto de borde brillante */
        .glowing-border {
          position: relative;
        }
        
        .glowing-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899);
          border-radius: inherit;
          z-index: -1;
          opacity: 0;
          transition: opacity 0.3s;
        }
        
        .glowing-border:hover::before {
          opacity: 1;
        }
      `}</style>
      {logoutCashModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            aria-label="Cerrar"
            onClick={() => setLogoutCashModalOpen(false)}
          />

          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-950/90 via-blue-950/70 to-slate-950/80 p-6 shadow-2xl backdrop-blur-2xl"
          >
            <div className="absolute -inset-20 bg-gradient-to-r from-blue-500/15 via-purple-500/10 to-transparent blur-3xl" />
            <div className="relative">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-amber-400/30 bg-amber-500/15 text-amber-100">
                  <LogOut className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-semibold text-white">Caja abierta pendiente</h3>
                  <p className="mt-1 text-sm text-white/80">
                    Tienes una caja abierta. Para entregar turno, primero cierra la caja.
                  </p>
                  {logoutCashMessage && (
                    <p className="mt-3 rounded-2xl border border-amber-300/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                      {logoutCashMessage}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setLogoutCashModalOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white/90 backdrop-blur-xl transition hover:bg-white/15"
                >
                  Cancelar
                </button>

                {isPrivilegedRole(roleName) && (
                  <button
                    type="button"
                    onClick={performLogout}
                    className="rounded-2xl border border-amber-300/20 bg-amber-500/15 px-5 py-2.5 text-sm font-semibold text-amber-100 transition hover:bg-amber-500/25"
                  >
                    Salir sin cerrar
                  </button>
                )}

                <button
                  type="button"
                  onClick={goCloseCashBeforeLogout}
                  className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300/50"
                >
                  Cerrar caja y entregar turno
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal glass para confirmación de cambio de almacén */}
      {warehouseConfirmOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Overlay */}
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            aria-label="Cerrar"
            onClick={cancelWarehouseChange}
          />

          {/* Card */}
          <div
            role="dialog"
            aria-modal="true"
            className={[
              "relative w-full max-w-3xl overflow-hidden rounded-3xl border border-white/10 shadow-2xl",
              "backdrop-blur-2xl",
              warehouseModal.wrap,
            ].join(" ")}
          >
            {/* Soft glow */}
            <div className="absolute -inset-24 bg-gradient-to-r from-white/10 via-white/5 to-transparent blur-3xl opacity-60" />

            <div className="relative p-6 sm:p-7">
              <div className="flex items-start gap-4">
                <div
                  className={[
                    "flex h-11 w-11 items-center justify-center rounded-2xl border",
                    "backdrop-blur-xl",
                    warehouseModal.ring,
                  ].join(" ")}
                >
                  <span className={["text-lg", warehouseModal.icon].join(" ")}>⚠️</span>
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-semibold text-white">Cambiar de almacén</h3>
                  <p className="mt-1 text-sm text-white/80">
                    Cambiar de almacén reiniciará el contexto de caja (sesión de caja) y puede limpiar carrito/cliente.
                    ¿Deseas continuar?
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={cancelWarehouseChange}
                  className="rounded-2xl bg-white/10 px-5 py-2.5 text-sm font-semibold text-white/90 hover:bg-white/15 border border-white/10 backdrop-blur-xl transition"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={confirmWarehouseChange}
                  className={[
                    "rounded-2xl px-5 py-2.5 text-sm font-semibold text-white",
                    "transition focus:outline-none focus:ring-2",
                    warehouseModal.button,
                  ].join(" ")}
                >
                  Aceptar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
